import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, Receipt, Users, Wallet, ArrowRight } from 'lucide-react';
import { Chart, CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Tooltip, Legend, Filler } from 'chart.js';
import api from '../../utils/api';
import { useAuth } from '../../store/auth';
import { inr } from '../../utils/format';
import {
    aggregateClientPortfolio,
    aggregateDashboardFinancials,
    buildSalesActivityFeed,
    buildCelebrations,
    topPerformers,
    monthlyClientTrend,
    filterSubmissions,
    fmtUpdatedLabel,
} from '../../utils/dashboardMetrics';
import {
    DashboardFilters,
    FinancialSummarySection,
    BreakdownCard,
    RecentActivityCard,
    CelebrationsCard,
    TrendLineChart,
    CollectionRateCard,
    TopPerformersSection,
    CLIENT_STATUS_COLOR,
} from '../../components/dashboard/DashboardWidgets';

Chart.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Tooltip, Legend, Filler);

const STATUS_PIE = [
    { key: 'approved', label: 'Approved' },
    { key: 'submitted', label: 'Pending' },
    { key: 'rejected', label: 'Rejected' },
    { key: 'draft', label: 'Draft' },
];

function clientStatusPie(counts) {
    return STATUS_PIE.map(s => ({
        label: s.label,
        value: counts[s.key] || 0,
        color: CLIENT_STATUS_COLOR[s.key],
    }));
}

function collectionPie(portfolio) {
    return [
        { label: 'Collected', value: portfolio.totalCollected, color: '#1a9a88' },
        { label: 'Balance', value: portfolio.totalBalance, color: '#d48484' },
    ];
}

export default function SalesDashboard() {
    const user = useAuth(s => s.user);
    const [k, setK] = useState({});
    const [reimb, setReimb] = useState([]);
    const [subs, setSubs] = useState([]);
    const [period, setPeriod] = useState('6m');
    const [status, setStatus] = useState('all');

    useEffect(() => {
        api.get('/dashboard/sales').then(r => setK(r.data)).catch(() => {});
        api.get('/reimbursements/me').then(r => setReimb(r.data.items || [])).catch(() => {});
        api.get('/forms/submissions/me').then(r => setSubs(r.data.items || [])).catch(() => {});
    }, []);

    const filteredSubs = useMemo(() => filterSubmissions(subs, { period, status }), [subs, period, status]);
    const portfolio = useMemo(() => aggregateClientPortfolio(filteredSubs), [filteredSubs]);
    const financials = useMemo(() => aggregateDashboardFinancials(filteredSubs), [filteredSubs]);
    const trend = useMemo(() => monthlyClientTrend(filteredSubs), [filteredSubs]);
    const performers = useMemo(() => topPerformers(filteredSubs, 5), [filteredSubs]);
    const monthlyPerformers = useMemo(() => {
        const start = new Date();
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        return topPerformers(filteredSubs.filter(s => new Date(s.createdAt) >= start), 5);
    }, [filteredSubs]);
    const activity = useMemo(() => buildSalesActivityFeed({ subs, reimb, limit: 5 }), [subs, reimb]);
    const celebrations = useMemo(
        () => buildCelebrations({
            users: user ? [user] : [],
            subs: filteredSubs,
            clientBasePath: '/sales/clients',
        }),
        [user, filteredSubs],
    );

    const pendingReimb = reimb.filter(r => r.status === 'submitted').length;
    const pendingItems = [
        { label: 'Pending review', count: portfolio.pendingReview },
        { label: 'Payments', count: portfolio.pendingPayments },
        { label: 'Expenses', count: pendingReimb },
        { label: 'Notifications', count: k.unreadNotifications ?? 0 },
    ].filter(i => i.count > 0);

    const firstName = user?.name?.split(' ')[0] || 'there';

    return (
        <div className="space-y-4 pb-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-sm font-extrabold font-heading text-ink-900 mr-1">Hi, {firstName}</h1>
                    <DashboardFilters period={period} onPeriodChange={setPeriod} status={status} onStatusChange={setStatus} />
                    <span className="text-[10px] text-ink-400 hidden sm:inline">{fmtUpdatedLabel()}</span>
                </div>
                <div className="flex items-center gap-2">
                    <Link to="/sales/kpi-analytics" className="ag-btn-ghost text-xs py-1.5">
                        Performance <ArrowRight className="h-3 w-3" />
                    </Link>
                    <Link to="/sales/clients/new" className="ag-btn-primary text-xs py-1.5">
                        <ClipboardList className="h-3.5 w-3.5" /> New client
                    </Link>
                    <Link to="/sales/reimbursements" className="ag-btn-ghost text-xs py-1.5">
                        <Receipt className="h-3.5 w-3.5" /> Expense
                    </Link>
                </div>
            </div>

            <FinancialSummarySection
                financials={financials}
                title="My financial summary"
                subtitle="Totals for your active approved clients"
                overdueLink="/sales/clients"
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch min-h-[360px]">
                <BreakdownCard
                    title="My portfolio"
                    sections={[
                        { title: 'Client status', segments: clientStatusPie(portfolio.counts), icon: Users },
                        { title: 'Collection split', segments: collectionPie(portfolio), icon: Wallet, formatValue: inr },
                    ]}
                />
                <RecentActivityCard
                    items={activity}
                    linkTo="/sales/clients"
                    linkLabel="All clients"
                />
                <CelebrationsCard events={celebrations} showTeamTab />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch min-h-[280px]">
                <TrendLineChart title="My clients over time" trend={trend} />
                <CollectionRateCard
                    rate={portfolio.collectionRate}
                    portfolio={portfolio}
                    pendingItems={pendingItems}
                    pendingLink="/sales/clients"
                />
            </div>

            <TopPerformersSection performers={performers} monthlyPerformers={monthlyPerformers} />
        </div>
    );
}
